/**
 * Who can see an item (media plan, kickoff, …): private, the whole team, or picked people.
 * Backed by the owner/visibility/shared_with columns every Cowork table carries; only the owner can change it.
 */
import { useEffect, useRef, useState } from 'react';
import { Lock, Users, UserPlus, Check, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { coworkApi, type SharingInfo, type SharingVisibility } from '@/cowork/omniad/api/coworkApi';

export function SharingControl({ sharing, onSave, onClaim, className }: {
  sharing: SharingInfo | null | undefined;
  onSave: (v: { visibility: SharingVisibility; sharedWith: string[] }) => Promise<unknown>;
  /** Legacy row with no owner: take ownership without changing who sees it. */
  onClaim?: () => Promise<unknown> | void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const members = useQuery({ queryKey: ['cowork', 'team-members'], queryFn: () => coworkApi.presentations.teamMembers(), staleTime: 10 * 60_000, enabled: open });
  useEffect(() => { setPicked(sharing?.sharedWith || []); }, [sharing?.sharedWith?.join(',')]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc); return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  if (!sharing) return null;
  const vis = sharing.visibility;
  const label = vis === 'team' || vis === 'public' ? 'Team' : vis === 'shared' || vis === 'members' ? `${sharing.sharedWith.length} people` : 'Only me';
  const Icon = vis === 'team' || vis === 'public' ? Users : vis === 'shared' || vis === 'members' ? UserPlus : Lock;
  const save = async (visibility: SharingVisibility, sharedWith: string[] = []) => {
    setBusy(true);
    try { await onSave({ visibility, sharedWith }); toast.success(visibility === 'private' ? 'Only you can see this' : visibility === 'team' ? 'Everyone on the team can see this' : `Shared with ${sharedWith.length} ${sharedWith.length === 1 ? 'person' : 'people'}`); setOpen(false); }
    catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  };
  const item = (v: SharingVisibility, text: string, hint: string, I: typeof Lock) => (
    <button type="button" disabled={busy} onClick={() => (v === 'shared' ? undefined : void save(v))} className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-[#f9fafb] ${vis === v ? 'bg-[#f9f5ff]' : ''}`}>
      <I className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#667085]" />
      <span className="min-w-0"><span className="block text-[12.5px] text-[#101828]" style={{ fontWeight: 600 }}>{text}</span><span className="block text-[11px] text-[#667085]">{hint}</span></span>
      {vis === v && <Check className="ml-auto h-3.5 w-3.5 text-[#7f56d9]" />}
    </button>
  );
  return (
    <div ref={ref} className={`relative ${className || ''}`}>
      <button type="button" onClick={() => sharing.isOwner && setOpen((o) => !o)} disabled={!sharing.isOwner} title={sharing.isOwner ? 'Who can see this' : 'Only the person who created this can change who sees it'} className="flex items-center gap-1.5 rounded-full border border-[#eaecf0] bg-white px-2.5 py-1 text-[11.5px] text-[#344054] hover:bg-[#f9fafb] disabled:cursor-default disabled:opacity-70" style={{ fontWeight: 600 }}>
        <Icon className="h-3.5 w-3.5" />{label}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1.5 w-[300px] rounded-xl border border-[#eaecf0] bg-white p-1.5 shadow-lg">
          {sharing.unowned && (
            <div className="mb-1 rounded-lg bg-[#fffaeb] px-2.5 py-2 text-[11px] text-[#93370d]">
              <div style={{ fontWeight: 600 }}>No owner yet</div>
              <div>Made before ownership existed. Choosing a setting below makes you the owner.</div>
              {onClaim && <button type="button" disabled={busy} onClick={async () => { setBusy(true); try { await onClaim(); toast.success('You are now the owner'); setOpen(false); } catch (e) { toast.error((e as Error).message); } finally { setBusy(false); } }} className="mt-1 underline" style={{ fontWeight: 600 }}>Make me the owner without changing who sees it</button>}
            </div>
          )}
          {item('private', 'Only me', 'Nobody else on the team sees it', Lock)}
          {item('team', 'Team', 'Everyone on the team can open and edit it', Users)}
          <div className={`rounded-lg px-2.5 py-2 ${vis === 'shared' || vis === 'members' ? 'bg-[#f9f5ff]' : ''}`}>
            <div className="flex items-center gap-2"><UserPlus className="h-3.5 w-3.5 text-[#667085]" /><span className="text-[12.5px] text-[#101828]" style={{ fontWeight: 600 }}>Specific people</span></div>
            <div className="mt-1.5 max-h-44 overflow-y-auto">
              {members.isLoading && <div className="flex items-center gap-1.5 px-1 py-1 text-[11px] text-[#667085]"><Loader2 className="h-3 w-3 animate-spin" /> Loading team…</div>}
              {(members.data?.members || []).map((m) => (
                <label key={m.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-[12px] text-[#344054] hover:bg-[#f2f4f7]">
                  <input type="checkbox" checked={picked.includes(m.id)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, m.id] : p.filter((x) => x !== m.id)))} />
                  <span className="truncate">{m.name}</span>{m.title && <span className="truncate text-[#98a2b3]">· {m.title}</span>}
                </label>
              ))}
            </div>
            <button type="button" disabled={busy || !picked.length} onClick={() => void save('shared', picked)} className="mt-1.5 w-full rounded-lg bg-[#7f56d9] px-3 py-1.5 text-[12px] text-white disabled:opacity-50" style={{ fontWeight: 600 }}>{busy ? 'Saving…' : `Share with ${picked.length || '…'}`}</button>
          </div>
        </div>
      )}
    </div>
  );
}
